(() => {
  if (window.HymShopifyFormsStyler) {
    window.HymShopifyFormsStyler.apply();
    return;
  }

  const HOST_STYLE_ID = 'hym-shopify-forms-host-styles';
  const ROOT_STYLE_ATTRIBUTE = 'data-hym-shopify-forms-styles';
  const observedRoots = new WeakSet();
  const pendingForms = new WeakMap();
  const measuredFormHeights = new WeakMap();
  const mobileQuery = window.matchMedia('(max-width: 767px)');
  let scheduled = false;

  const ensureHostStyles = () => {
    if (document.getElementById(HOST_STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = HOST_STYLE_ID;
    style.textContent = `
      shopify-forms-embed {
        --inline-container-max-width: 720px;
      }

      @media screen and (max-width: 767px) {
        shopify-forms-embed {
          --inline-container-max-width: calc(100vw - 32px);
        }
      }
    `;
    document.head.appendChild(style);
  };

  const rememberFormHeight = (embed, appRoot) => {
    if (appRoot.dataset.currentStep !== 'form') return;

    window.requestAnimationFrame(() => {
      if (appRoot.dataset.currentStep !== 'form') return;

      const height = Math.ceil(appRoot.getBoundingClientRect().height);
      if (height < 300) return;

      const measuredHeight = Math.max(measuredFormHeights.get(embed) || 0, height);
      measuredFormHeights.set(embed, measuredHeight);
      appRoot.style.setProperty('--hym-form-reserved-height', `${measuredHeight}px`);
    });
  };

  const preserveSuccessHeight = (embed, appRoot) => {
    const measuredHeight = measuredFormHeights.get(embed);

    if (measuredHeight) {
      appRoot.style.setProperty('--hym-form-reserved-height', `${measuredHeight}px`);
    }
  };

  const styleForm = (embed) => {
    const root = embed.shadowRoot;
    const appRoot = root?.querySelector('#app-embed');

    if (!root || !appRoot) return false;

    const headingSize = mobileQuery.matches ? '24px' : '28px';
    const bodySize = mobileQuery.matches ? '14px' : '15px';
    const properties = {
      '--forms-heading-font': `normal 700 ${headingSize} Lexend, sans-serif`,
      '--forms-body-font': `normal 400 ${bodySize} Lexend, sans-serif`,
      '--forms-base-font-size': bodySize,
      '--heading-text-color': '#003185',
      '--body-text-color': '#303747',
      '--form-text-color': '#303747',
      '--form-placeholder-color': '#7b8493',
      '--button-background-color': '#003185',
      '--button-text-color': '#ffffff',
      '--button-border-radius': '6px',
      '--link-text-color': '#1878b9',
      '--forms-accent-color': '#1878b9',
      '--form-field-border': '0 0 0 1px #cfd8e3',
      '--form-field-border-focused': '0 0 0 2px #1878b9',
      '--form-field-border-radius': '6px'
    };

    Object.entries(properties).forEach(([property, value]) => {
      appRoot.style.setProperty(property, value);
    });

    if (!root.querySelector(`style[${ROOT_STYLE_ATTRIBUTE}]`)) {
      const style = document.createElement('style');
      style.setAttribute(ROOT_STYLE_ATTRIBUTE, '');
      style.textContent = `
        fieldset > legend {
          font-weight: 700 !important;
        }

        select {
          width: 100%;
          min-height: 50px;
          padding: 0 44px 0 15px;
          border: 0;
          border-radius: var(--form-field-border-radius);
          background-color: #ffffff;
          box-shadow: var(--form-field-border);
          color: var(--form-text-color);
          font: var(--forms-body-font);
        }

        select:focus-visible {
          outline: 0;
          box-shadow: var(--form-field-border-focused);
        }

        select option {
          color: var(--form-text-color);
          font: var(--forms-body-font);
        }

        #app-embed[data-current-step="success"] [data-testid="app-router"] {
          display: flex;
          min-height: var(--hym-form-reserved-height, 690px);
          align-items: center;
          justify-content: center;
        }

        #app-embed[data-current-step="success"] [data-testid="app-router"] > * {
          width: 100%;
        }

        @media screen and (max-width: 767px) {
          #app-embed[data-current-step="success"] [data-testid="app-router"] {
            min-height: var(--hym-form-reserved-height, 760px);
          }
        }
      `;
      root.appendChild(style);
    }

    if (appRoot.dataset.currentStep === 'success') {
      preserveSuccessHeight(embed, appRoot);
    } else {
      rememberFormHeight(embed, appRoot);
    }

    if (!observedRoots.has(root)) {
      new MutationObserver(() => scheduleApply()).observe(root, {
        childList: true,
        subtree: true
      });
      observedRoots.add(root);
    }

    return true;
  };

  const ensureFormStyled = (embed) => {
    if (styleForm(embed)) {
      const pending = pendingForms.get(embed);
      if (pending?.timer) window.clearTimeout(pending.timer);
      pendingForms.delete(embed);
      return;
    }

    const pending = pendingForms.get(embed) || { attempts: 0, timer: null };
    if (pending.timer || pending.attempts >= 80) return;

    pending.timer = window.setTimeout(() => {
      pending.timer = null;
      pending.attempts += 1;
      pendingForms.set(embed, pending);
      ensureFormStyled(embed);
    }, 250);
    pendingForms.set(embed, pending);
  };

  const apply = () => {
    ensureHostStyles();
    document.querySelectorAll('shopify-forms-embed').forEach(ensureFormStyled);
  };

  const scheduleApply = () => {
    if (scheduled) return;
    scheduled = true;

    window.requestAnimationFrame(() => {
      scheduled = false;
      apply();
    });
  };

  window.HymShopifyFormsStyler = { apply };

  new MutationObserver(scheduleApply).observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  mobileQuery.addEventListener('change', scheduleApply);
  document.addEventListener('shopify:section:load', scheduleApply);
  document.addEventListener('DOMContentLoaded', scheduleApply, { once: true });
  apply();
})();
